import React from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { useCurrentOrgContext } from './use-current-org';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

interface UseSignalROptions {
  hubPath: string;
  enabled?: boolean;
}

interface UseSignalRResult {
  connection: HubConnection | null;
  status: ConnectionStatus;
}

interface ManagedConnection {
  connection: HubConnection;
  refCount: number;
  disposeTimer: ReturnType<typeof setTimeout> | null;
}

const connections = new Map<string, ManagedConnection>();
let _latestGetToken: (() => Promise<string | null>) | null = null;

export function useSignalR({ hubPath, enabled = true }: UseSignalROptions): UseSignalRResult {
  const { getToken } = useAuth();
  const { currentOrg } = useCurrentOrgContext();
  const [status, setStatus] = React.useState<ConnectionStatus>('disconnected');
  const connectionRef = React.useRef<HubConnection | null>(null);

  _latestGetToken = getToken;

  const hubKey = enabled && currentOrg?.orgId
    ? `${hubPath}:${currentOrg.orgId}`
    : null;

  React.useEffect(() => {
    if (!hubKey) {
      connectionRef.current = null;
      setStatus('disconnected');
      return;
    }

    let managed = connections.get(hubKey);

    if (managed) {
      // Connection exists — cancel any pending dispose and reuse
      if (managed.disposeTimer) {
        clearTimeout(managed.disposeTimer);
        managed.disposeTimer = null;
      }
      managed.refCount++;
      connectionRef.current = managed.connection;

      const state = managed.connection.state;
      if (state === HubConnectionState.Connected) setStatus('connected');
      else if (state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting) setStatus('connecting');
      else setStatus('disconnected');
    } else {
      // Create new connection
      const bffBaseUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_BASE_URL as string) || '';
      const hubUrl = `${bffBaseUrl.replace(/\/$/, '')}${hubPath}?orgId=${encodeURIComponent(currentOrg!.orgId)}`;

      const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await (_latestGetToken?.() ?? Promise.resolve(null));
            return token ?? '';
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

      managed = { connection, refCount: 1, disposeTimer: null };
      connections.set(hubKey, managed);
      connectionRef.current = connection;

      connection.onreconnecting(() => setStatus('reconnecting'));
      connection.onreconnected(() => setStatus('connected'));
      connection.onclose(() => setStatus('disconnected'));

      setStatus('connecting');
      connection
        .start()
        .then(() => setStatus('connected'))
        .catch(() => setStatus('disconnected'));
    }

    return () => {
      const m = connections.get(hubKey);
      if (!m) return;

      m.refCount--;
      if (m.refCount <= 0) {
        // Delay disposal to survive React Strict Mode unmount/remount cycle
        m.disposeTimer = setTimeout(() => {
          m.connection.stop();
          connections.delete(hubKey);
        }, 100);
      }
      connectionRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubKey]);

  return {
    connection: connectionRef.current,
    status,
  };
}
